// ProduccionLayout.tsx
import React, { useState, useEffect } from 'react';
import { Layout, Row, Col, Card, Typography, message, InputNumber, Button, Radio, Space, Spin } from 'antd';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import { useProduccionContext } from '../../context/ProduccionContext';
import { ClockCircleOutlined, LoadingOutlined } from '@ant-design/icons';

const { Title } = Typography;

type RouteParams = {
  tickets1?: string;
  tickets2?: string;
  tickets3?: string;
  tickets4?: string;
  productoId?: string;
}

interface DatosArea {
  titulo: string;
  datos: Record<string, any>;
}


interface Fardo {
  numero: number;
  kilos: number;
  fechaHora: string;
  indice: string;
}

interface TaraFardo {
  id: number;
  id_empresa: number;
  id_sucursal: number;
  id_producto: number;
  tara: number;
}

interface User {
  id_empresa: number;
  id_sucursal: number;
  // ... otros campos del usuario
}

interface DatosFijos {
  id: number;
  id_empresa: number;
  id_sucursal: number;
  alta: number;
}

interface TicketData {
  id_balanza: number;
  numero_ticket: number;
  fecha: string;
  cliente: string;
  productor: string;
  kilos: number;
  rollos: Array<{
    id_rollo: number | null;
    rollo_identificacion: string | null;
  }>;
}

const ProduccionLayout: React.FC = () => {
  const { produccionData, clearProduccionData } = useProduccionContext();
  const navigate = useNavigate();
  
  // Extraer datos del contexto
  const tickets = produccionData?.tickets || [];
  const productoId = produccionData?.productoId || '';
  const arrayDatosCompletos = produccionData?.arrayDatosCompletos || [];
  
  // Verificar si hay datos válidos para procesar
  const hayDatosValidos = produccionData && (
    produccionData.tickets.length > 0 || 
    produccionData.arrayDatosCompletos.length > 0
  );

  // Estado para el reloj de espera
  const [tiempoEspera, setTiempoEspera] = useState(0);
  const [mostrarRelojEspera, setMostrarRelojEspera] = useState(false);
  const [fardoEsperado, setFardoEsperado] = useState<number | null>(null);
  const [fardoValidado, setFardoValidado] = useState(false);
  const [validandoFardo, setValidandoFardo] = useState(false);
  const [primerFardoValidado, setPrimerFardoValidado] = useState(false);

  // Procesar datos enviados desde A_Produccion a través del contexto
  useEffect(() => {
    console.log('=== INICIO PROCESAMIENTO DE DATOS ===');
    console.log('Estado actual de produccionData:', produccionData);
    console.log('Estado actual de hayDatosValidos:', hayDatosValidos);
    
    if (produccionData && produccionData.arrayDatosCompletos && produccionData.arrayDatosCompletos.length > 0) {
      console.log('✅ DATOS VÁLIDOS ENCONTRADOS EN EL CONTEXTO');
      console.log('Datos de producción recibidos desde contexto:', produccionData);
      console.log('Array de datos completos recibido:', produccionData.arrayDatosCompletos);
      console.log('Total de registros en arrayDatosCompletos:', produccionData.arrayDatosCompletos.length);
      
      // Log detallado de cada registro
      console.log('=== VERIFICACIÓN ARRAY DATOS COMPLETOS ===');
      produccionData.arrayDatosCompletos.forEach((registro, index) => {
        console.log(`Registro ${index + 1}:`, {
          numero_ticket: registro.numero_ticket,
          rollo_identificacion: registro.rollo_identificacion,
          fecha: registro.fecha,
          cliente: registro.cliente,
          productor: registro.productor,
          producto: registro.producto,
          kilos: registro.kilos
        });
      });
      console.log('=== FIN VERIFICACIÓN ===');
      
      // Usar los datos del contexto
      setDatosTickets(produccionData.tickets);
      setDetalleTickets(produccionData.tickets);
      setProductoNivel(produccionData.productoNivel);

      // Determinar clienteIdFardo según compra_servicio (igual que el backend en alta_produccion):
      // si al menos un ticket es Compra → usar clienteCero; si todos son Servicio → cliente real del ticket.
      // Esto aplica para cualquier nivel; para nivel != 1 el effect posterior sobreescribe con clienteCero.
      if (produccionData.arrayDatosCompletos.length > 0) {
        const hayCompra = produccionData.arrayDatosCompletos.some(r => r.compra_servicio === 'C');
        if (!hayCompra) {
          // Todos servicio: usar cliente real del primer ticket (de Balanza)
          setClienteIdFardo(produccionData.arrayDatosCompletos[0].cliente_id);
        }
        // Si hayCompra, clienteIdFardo se setea en el effect de clienteCero (nivel != 1 o al cargar clienteCero)
        setPresentacionIdFardo(produccionData.arrayDatosCompletos[0].presentacion_id);
      }
      
      // Ocultar reloj de espera cuando hay datos
      setMostrarRelojEspera(false);
      setTiempoEspera(0);
      
      message.success('Datos de tickets cargados desde selección');
      console.log('✅ DATOS PROCESADOS EXITOSAMENTE');
    } else {
      console.log('❌ NO HAY DATOS VÁLIDOS EN EL CONTEXTO');
      console.log('produccionData:', produccionData);
      console.log('arrayDatosCompletos length:', produccionData?.arrayDatosCompletos?.length || 0);
      
      // Mostrar reloj de espera cuando no hay datos
      if (!produccionData) {
        console.log('Esperando datos del contexto...');
        setMostrarRelojEspera(true);
      } else if (!produccionData.arrayDatosCompletos || produccionData.arrayDatosCompletos.length === 0) {
        console.log('Contexto existe pero arrayDatosCompletos está vacío');
        setMostrarRelojEspera(true);
        message.info('Esperando datos de tickets...');
      }
    }
    console.log('=== FIN PROCESAMIENTO DE DATOS ===');
  }, [produccionData]);

  // Reloj de espera
  useEffect(() => {
    let intervalo: NodeJS.Timeout;
    
    if (mostrarRelojEspera) {
      intervalo = setInterval(() => {
        setTiempoEspera(prev => prev + 1);
      }, 1000);
    }
    
    return () => {
      if (intervalo) {
        clearInterval(intervalo);
      }
    };
  }, [mostrarRelojEspera]);

  // Función para formatear el tiempo de espera
  const formatearTiempoEspera = (segundos: number): string => {
    const minutos = Math.floor(segundos / 60);
    const segs = segundos % 60;
    return `${minutos.toString().padStart(2, '0')}:${segs.toString().padStart(2, '0')}`;
  };

  console.log('Datos del contexto procesados:', {
    ticketsLength: tickets.length,
    arrayDatosCompletosLength: arrayDatosCompletos.length,
    productoId,
    'URL actual': window.location.pathname
  });
  
  console.log('Estado del contexto:', {
    produccionData: !!produccionData,
    hayDatosValidos,
    ticketsLength: tickets.length,
    arrayDatosCompletosLength: arrayDatosCompletos.length
  });
  
  // Log adicional para verificar arrayDatosCompletos en tiempo real
  console.log('Estado actual de arrayDatosCompletos:', {
    length: arrayDatosCompletos.length,
    data: arrayDatosCompletos
  });
  
  // Log para verificar qué datos se están usando
  console.log('Datos a usar para cálculos:', {
    usandoContexto: arrayDatosCompletos.length > 0,
    ticketsValidos: arrayDatosCompletos.length
  });

  // Agregar estado para los detalles de tickets
  const [detalleTickets, setDetalleTickets] = useState<any[]>([]);
  
  // Estado para los datos de tickets enviados desde A_Produccion
  const [datosTickets, setDatosTickets] = useState<TicketData[]>([]);

  const { getAccessTokenSilently, user } = useAuth0();
  const [productoDesc, setProductoDesc] = useState<string>('');
  const [productoNivel, setProductoNivel] = useState<string>('');
  const [clienteCero, setClienteCero] = useState<any>(null);

  const [datosA3, setDatosA3] = useState({
    'Tickets a Procesar': '',
    'Kilos a Procesar': '',
    'Tara p/Fardo': 0,
    'Cantidad Fardos': 0,
    'Kilos Brutos Fardos': 0,
    'Kilos Tara Fardos': 0,
    'Kilos Netos Fardos': 0,
    'Rinde %': '0.00%',
    'Kilos Semilla Calculo': 0
  });

  // Agregamos estado para el número de fardo y kilos
  const [fardoNumero, setFardoNumero] = useState(1);
  const [fardoNumeroCopia, setFardoNumeroCopia] = useState(1);
  // Lista de números de fardo ya existentes en DB para el cliente/producto/ciclo activo.
  // Permite detectar huecos (ej: existen 1,3 → 2 está disponible aunque 2 < MAX=3).
  const [fardosExistentesDB, setFardosExistentesDB] = useState<number[]>([]);
  // Mapa nro_fardo → numero_produccion para incluir en mensajes de error.
  const [fardoProduccionMap, setFardoProduccionMap] = useState<Record<number, number>>({});
  const [kilosFardo, setKilosFardo] = useState<number | null>(null);
  const [fardos, setFardos] = useState<Fardo[]>([]);
  // Ref para acceder al array de fardos dentro de effects sin generar dependencias circulares
  const fardosRef = React.useRef<Fardo[]>([]);
  const [fardoSeleccionado, setFardoSeleccionado] = useState<number | null>(null);

  // Mantener ref sincronizado con el estado de fardos
  useEffect(() => {
    fardosRef.current = fardos;
  }, [fardos]);

  // Agregamos estados para los restos
  const [restoNegativo, setRestoNegativo] = useState<number | null>(null);
  const [restoPositivo, setRestoPositivo] = useState<number | null>(null);
  
  // Estado para el coeficiente de semilla
  const [coeficienteSemilla, setCoeficienteSemilla] = useState<number>(1.4);

  // Agregamos estado para modo edición
  const [modoEdicion, setModoEdicion] = useState(false);



  // Mantener solo el estado para el efecto de reloj del botón
  const [isButtonLoading, setIsButtonLoading] = useState(false);
  const [isGrabarTodoEnabled, setIsGrabarTodoEnabled] = useState(false);



  // Agregar estado para el nuevo número de fardo
  const [nuevoNumeroFardo, setNuevoNumeroFardo] = useState<number | null>(null);

  // ID de cliente a usar para la consulta de numeración de fardos
  const [clienteIdFardo, setClienteIdFardo] = useState<number | null>(null);

  // ID de presentacion (granel/módulo/rollo) para unicidad de fardos
  const [presentacionIdFardo, setPresentacionIdFardo] = useState<number | null>(null);

  // Log de datos del contexto
  useEffect(() => {
    console.log('Datos del contexto recibidos:', {
      ticketsLength: tickets.length,
      arrayDatosCompletosLength: arrayDatosCompletos.length,
      productoId
    });
  }, [tickets.length, arrayDatosCompletos.length, productoId]);

  // clienteIdFardo depende SOLO de compra_servicio (independiente del nivel del producto):
  //   COMPRA → clienteCero (counter compartido de la empresa)
  //   SERVICIO → cliente real del ticket (counter propio del productor)
  useEffect(() => {
    if (!clienteCero || !arrayDatosCompletos.length) return;
    const hayCompra = arrayDatosCompletos.some((r: any) => r.compra_servicio === 'C');
    if (hayCompra) {
      setClienteIdFardo(clienteCero.id);
      setPresentacionIdFardo(arrayDatosCompletos[0].presentacion_id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clienteCero, arrayDatosCompletos]);

  // Cargar coeficiente de semilla desde la API
  useEffect(() => {
    const cargarCoeficienteSemilla = async () => {
      if (!user?.id_empresa || !user?.id_sucursal) {
        return;
      }

      try {
        const token = await getAccessTokenSilently();
        
        const response = await fetch(
          `${process.env.REACT_APP_RELACION_URL}/leer_datos_fijos/${user.id_empresa}/${user.id_sucursal}/SEM`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const result = await response.json();
        
        if (result && result.length > 0 && result[0].valor_numerico) {
          // Convertir el valor numérico de centésimas a decimal
          setCoeficienteSemilla(result[0].valor_numerico / 100);
          console.log('Coeficiente de semilla cargado:', result[0].valor_numerico / 100);
        } else {
          console.log('No se encontró coeficiente de semilla, usando valor por defecto 1.4');
          setCoeficienteSemilla(1.4);
        }
      } catch (error) {
        console.error('Error al cargar coeficiente de semilla:', error);
        setCoeficienteSemilla(1.4); // Valor por defecto
      }
    };

    cargarCoeficienteSemilla();
  }, [getAccessTokenSilently, user?.id_empresa, user?.id_sucursal]);

  // Función para verificar el estado de calificación
  const verificarEstadoCalificacion = async () => {
    try {
      if (!user) {
        console.error('No hay usuario autenticado');
        return false;
      }

      const userMetadata = user as unknown as User;
      const id_empresa = userMetadata.id_empresa;
      const id_sucursal = userMetadata.id_sucursal;

      const token = await getAccessTokenSilently();
      const response = await axios.get<DatosFijos[]>(
        `${process.env.REACT_APP_RELACION_URL}/leer_datos_fijos/${id_empresa}/${id_sucursal}/PRE`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Si no hay datos o está vacío, EC = false
      if (!response.data || response.data.length === 0) {
        console.log('No hay datos fijos, EC = false');
        // setEstadoCalificacion(false); // Eliminado
        return false;
      }

      // Si alta = 0, EC = false
      if (response.data[0].alta === 0) {
        console.log('Alta = 0, EC = false');
        // setEstadoCalificacion(false); // Eliminado
        return false;
      }

      // Si alta = 1, EC = true
      console.log('Alta = 1, EC = true');
      // setEstadoCalificacion(true); // Eliminado
      return true;

    } catch (error) {
      console.error('Error al verificar estado de calificación:', error);
      message.error('Error al verificar estado de calificación');
      // setEstadoCalificacion(false); // Eliminado
      return false;
    }
  };

  // Actualizamos el useEffect para cargar el producto
  useEffect(() => {
    const fetchProducto = async () => {
      try {
        const token = await getAccessTokenSilently();
        
        const response = await fetch(
          `${process.env.REACT_APP_BALANZA_URL}/productos_id/${productoId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        
        const data = await response.json();
        console.log('Datos del producto:', data);

        if (data && data.descripcion) {
          setProductoDesc(data.descripcion);
          setProductoNivel(data.nivel || '');
          console.log('Nivel del producto:', data.nivel);
          
          // Si el producto es tipo 1, verificar EC
          if (data.tipo === 1) {
            console.log('Producto tipo 1, verificando EC');
            await verificarEstadoCalificacion();
            await fetchTaraFardo(); // Cargar tara después de verificar EC
          } else {
            console.log('Producto no es tipo 1, EC = false');
            // setEstadoCalificacion(false); // Eliminado
          }
        }
      } catch (error) {
        console.error('Error al cargar producto:', error);
        setProductoDesc('Error al cargar producto');
        // setEstadoCalificacion(false); // Eliminado
      }
    };

    if (productoId && user) {
      fetchProducto();
    }
  }, [productoId, user]);

  // Función para buscar detalles de tickets - SOLO si no hay datos del contexto
  useEffect(() => {
    // Si hay datos del contexto, no hacer la llamada a la API
    if (arrayDatosCompletos.length > 0) {
      console.log('✅ Usando datos del contexto, no se hace llamada a la API');
      return;
    }

    // Si no hay datos del contexto, esperar
    console.log('⏳ Esperando datos del contexto...');
  }, [arrayDatosCompletos.length]);

  // Actualizamos el useEffect para incluir el cálculo del Rinde
  useEffect(() => {
    // Usar ÚNICAMENTE datos del contexto
    if (arrayDatosCompletos && arrayDatosCompletos.length > 0) {
      const ticketsValidos = arrayDatosCompletos.length;
      
      const kilosTotales = arrayDatosCompletos.reduce((sum, ticket) => {
        return sum + (Number(ticket?.kilos) || 0);
      }, 0);

      // Calculamos separando fardos regulares de restos
      const fardosRegulares = fardos.filter(f => f.numero !== 0);
      const restos = fardos.filter(f => f.numero === 0);

      // Kilos totales (incluyendo restos)
      const kilosBrutosFardos = fardos.reduce((sum, fardo) => sum + fardo.kilos, 0);
      
      // Cantidad de fardos (excluyendo restos)
      const cantidadFardos = fardosRegulares.length;
      
      // Tara total (solo para fardos regulares)
      const kilosTaraFardos = cantidadFardos * datosA3['Tara p/Fardo'];
      
      const kilosNetosFardos = kilosBrutosFardos - kilosTaraFardos;

      // Calculamos el Rinde %
      const rinde = kilosTotales > 0 
        ? ((kilosNetosFardos || 0) * 100 / (kilosTotales || 1)).toFixed(2)
        : '0.00';

      // Calcular Kilos Semilla: mismo criterio que el backend (_recalcular_produccion,
      // ajustar_semilla_preview) -> solo productos cuya descripción sea exactamente "FIBRA".
      let kilosSemilla = 0;
      console.log(`Calculando semilla - Producto: ${productoDesc}, Kilos Netos: ${kilosNetosFardos}`);

      if (productoDesc === 'FIBRA') {
        kilosSemilla = kilosNetosFardos * coeficienteSemilla;
        console.log(`FIBRA - Cálculo de semilla: ${kilosNetosFardos} * ${coeficienteSemilla} = ${kilosSemilla}`);
        if (kilosSemilla === 0 && kilosNetosFardos > 0) {
          console.warn('Kilos Semilla es 0 para producto FIBRA');
          message.warning('Advertencia: Kilos Semilla es 0 para producto FIBRA');
        }
      } else {
        // Productos que no son FIBRA no calculan semilla
        kilosSemilla = 0;
        console.log(`Producto ${productoDesc} no es FIBRA, kilos semilla = 0 (no se calcula)`);
      }

      setDatosA3(prevState => ({
        ...prevState,
        'Tickets a Procesar': `${ticketsValidos} tickets`,
        'Kilos a Procesar': `${kilosTotales.toLocaleString()} kg`,
        'Cantidad Fardos': cantidadFardos,
        'Kilos Brutos Fardos': kilosBrutosFardos,
        'Kilos Tara Fardos': kilosTaraFardos,
        'Kilos Netos Fardos': kilosNetosFardos,
        'Rinde %': `${rinde}%`,
        'Kilos Semilla Calculo': Math.round(kilosSemilla * 100) / 100 // Redondear a 2 decimales
      }));
    }
   }, [arrayDatosCompletos, fardos, datosA3['Tara p/Fardo'], productoDesc, coeficienteSemilla]);

  // Función para formatear la fecha y hora
  const formatearFechaHora = (fecha: Date): string => {
    const year = fecha.getFullYear();
    const month = String(fecha.getMonth() + 1).padStart(2, '0');
    const day = String(fecha.getDate()).padStart(2, '0');
    const hours = String(fecha.getHours()).padStart(2, '0');
    const minutes = String(fecha.getMinutes()).padStart(2, '0');
    const seconds = String(fecha.getSeconds()).padStart(2, '0');
    
    return `${year}-${month}-${day}  ${hours}:${minutes}:${seconds}`;
  };

  // Función para validar el número de fardo (solo el primer fardo)
  const validarNumeroFardo = async () => {
    try {
      setValidandoFardo(true);
      
      if (!user || !productoId || !clienteIdFardo || !presentacionIdFardo) {
        console.error('No hay usuario, producto, cliente o presentacion para validar fardo');
        return false;
      }

      const userMetadata = user as unknown as User;
      const id_empresa = userMetadata.id_empresa;
      const id_sucursal = userMetadata.id_sucursal;

      const token = await getAccessTokenSilently();

      const response = await fetch(
        `${process.env.REACT_APP_PRODUCCION_URL}/ultimo_fardo_desmotado/${id_empresa}/${id_sucursal}/${productoId}/${clienteIdFardo}/${presentacionIdFardo}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Error al validar número de fardo');
      }

      const data = await response.json();

      if (data.status === 'success') {
        const existentes: number[] = data.fardos_existentes || [];
        const mapaProduccion: Record<number, number> = data.fardos_con_produccion || {};
        setFardosExistentesDB(existentes);
        setFardoProduccionMap(mapaProduccion);

        // El número esperado es el primero disponible desde el máximo + 1
        const ultimoFardo = data.ultimo_fardo;
        const fardoEsperado = ultimoFardo + 1;

        setFardoValidado(true);
        setPrimerFardoValidado(true);
        setFardoEsperado(fardoEsperado);

        // Corregir solo si el número actual YA existe en DB
        if (existentes.includes(fardoNumero)) {
          const nroProd = mapaProduccion[fardoNumero];
          const detalle = nroProd ? ` (Producción N° ${nroProd})` : '';
          setFardoNumero(fardoEsperado);
          setFardoNumeroCopia(fardoEsperado);
          message.warning(`Primer fardo corregido a: ${fardoEsperado}. El N° ${fardoNumero} ya existe${detalle}`);
        }
        return fardoEsperado;
      } else {
        console.error('Error en respuesta de validación:', data.message);
        setFardoValidado(false);
        return false;
      }
    } catch (error) {
      console.error('Error al validar número de fardo:', error);
      setFardoValidado(false);
      message.error('Error al validar el número de fardo');
      return false;
    } finally {
      setValidandoFardo(false);
    }
  };

  // Modificar la función grabarFardo para incluir validación solo del primer fardo
  const grabarFardo = async () => {
    if (!kilosFardo || kilosFardo <= 0) {
      message.error('Debe ingresar los kilos');
      return;
    }

    // Solo validar si es el primer fardo y no está validado
    let numeroFardo = fardoNumero;

    if (fardos.some(f => f.numero === numeroFardo)) {
      message.error(`El fardo N° ${numeroFardo} ya fue grabado en esta sesión`);
      return;
    }

    if (fardosExistentesDB.includes(numeroFardo)) {
      const nroProd = fardoProduccionMap[numeroFardo];
      const detalle = nroProd ? ` (Producción N° ${nroProd})` : '';
      message.error(`El fardo N° ${numeroFardo} ya existe en la base de datos${detalle}`);
      return;
    }

    if (fardos.length === 0 && !primerFardoValidado) {
      const numeroValidado = await validarNumeroFardo();
      if (numeroValidado === false) {
        message.error('Error al validar el número de fardo');
        return;
      }
      numeroFardo = numeroValidado;
    }

    const nuevoFardo: Fardo = {
      numero: numeroFardo,
      kilos: kilosFardo,
      fechaHora: formatearFechaHora(new Date()),
      indice: 'F'
    };

    setFardos([...fardos, nuevoFardo]);
    setFardoNumero(numeroFardo + 1);
    setKilosFardo(null);
    setFardoValidado(true); // Siempre válido después del primer fardo
    message.success('Fardo grabado correctamente');

    // Activar el botón Grabar Todo si es el primer fardo
    if (!isGrabarTodoEnabled) {
      setIsGrabarTodoEnabled(true);
    }
  };

  // Función para seleccionar fardo para edición
  const handleSeleccionFardo = (numeroFardo: number) => {
    const fardoSeleccionado = fardos.find(f => f.numero === numeroFardo);
    
    if (fardoSeleccionado) {
      if (fardoSeleccionado.numero === 0) {
        // Es un resto
        const restos = fardos.filter(f => f.numero === 0);
        
        // Buscar y setear resto negativo
        const restoNeg = restos.find(r => r.indice === '-');
        if (restoNeg) {
          setRestoNegativo(Math.abs(restoNeg.kilos));
        }
        
        // Buscar y setear resto positivo
        const restoPos = restos.find(r => r.indice === '+');
        if (restoPos) {
          setRestoPositivo(restoPos.kilos);
        }
        
        setModoEdicion(false);
      } else {
        // Es un fardo normal
        setFardoNumero(fardoSeleccionado.numero);
        setKilosFardo(fardoSeleccionado.kilos);
        setModoEdicion(true);
        setRestoNegativo(null);
        setRestoPositivo(null);
      }
    }
  };

  // Función para modificar fardo
  const modificarFardo = () => {
    if (!kilosFardo || kilosFardo <= 0) {
      message.error('Debe ingresar los kilos');
      return;
    }

    setFardos(fardos.map(fardo => 
      fardo.numero === fardoNumero
        ? {
            ...fardo,
            kilos: kilosFardo,
            fechaHora: formatearFechaHora(new Date())
          }
        : fardo
    ));

    setModoEdicion(false);
    setKilosFardo(null);
    setFardoNumero(fardoNumeroCopia+fardos.length);
    console.log('Fardo seleccionado:', fardoNumero,fardos.length+1);
    setFardoSeleccionado(null);
    message.success('Fardo modificado correctamente');
  };



  // Función para grabar todo
  const grabarTodo = async () => {
    try {
      if (fardos.length === 0) {
        message.error('No hay producción');
        return;
      }

      // Validación adicional para productos FIBRA (mismo criterio que el cálculo de semilla)
      console.log(`Validando producto: ${productoDesc}, kilos semilla: ${datosA3['Kilos Semilla Calculo']}`);

      if (productoDesc === 'FIBRA') {
        console.log('Producto es FIBRA, validando kilos semilla...');
        if (datosA3['Kilos Semilla Calculo'] === 0) {
          console.log('Error: Kilos semilla es 0 para producto FIBRA');
          message.error('Error: Para productos FIBRA, el cálculo de semilla no puede ser 0');
          return;
        }
        console.log('Validación de kilos semilla exitosa para FIBRA');
      } else {
        // Productos que no son FIBRA no validan kilos_semilla (puede ser 0)
        console.log(`Producto ${productoDesc} no es FIBRA, no se valida kilos semilla (puede ser 0)`);
      }

      // Determinar qué cliente usar según el nivel del producto
       let clienteId: number;
       let productorId: number;

       // Usar ÚNICAMENTE datos del contexto
       if (arrayDatosCompletos.length > 0) {
         // Obtener los IDs desde el primer registro de arrayDatosCompletos
         const primerRegistro = arrayDatosCompletos[0];
         clienteId = primerRegistro.cliente_id;
         productorId = primerRegistro.productor_id;
         
         console.log('Datos del contexto:', primerRegistro);
         console.log('Cliente del contexto:', primerRegistro.cliente, 'ID:', primerRegistro.cliente_id);
         console.log('Productor del contexto:', primerRegistro.productor, 'ID:', primerRegistro.productor_id);
         console.log(`Usando datos del contexto - Cliente ID: ${clienteId}, Productor ID: ${productorId}`);
         
         // Validar que los IDs sean válidos
         if (!clienteId || clienteId <= 0 || !productorId || productorId <= 0) {
           console.error('IDs inválidos:', { clienteId, productorId });
           message.error('Error: Los IDs de cliente o productor no son válidos. Por favor, seleccione los tickets nuevamente.');
           return;
         }
       } else {
         console.error('No hay datos del contexto para procesar');
         message.error('Error: No hay datos del contexto para procesar');
         return;
       }

      const hayCompraEnLote = arrayDatosCompletos.some(r => r.compra_servicio === 'C');
      if (hayCompraEnLote) {
        // COMPRA: usar clienteCero (empresa) — independiente del nivel del producto
        if (clienteCero) {
          clienteId = clienteCero.id;
          productorId = clienteCero.id;
          console.log(`Usando cliente cero (NC='000000'): ${clienteCero.denominacion}`);
          console.log(`Cliente ID: ${clienteId}, Productor ID: ${productorId}`);
        } else {
          message.error('Error: No se encontró el cliente con NC="000000" para este producto');
          return;
        }
      } else {
        // SERVICIO: usar cliente real del ticket — independiente del nivel del producto
        console.log(`Usando cliente del contexto - Cliente ID: ${clienteId}, Productor ID: ${productorId}`);
      }

      setIsButtonLoading(true); // Activar solo el efecto de carga en el botón
      message.loading('Grabando producción...', 0);

      const token = await getAccessTokenSilently();

      // Obtener fecha/hora inicio y final del array de fardos
      const fechaHoraInicio = fardos[0].fechaHora;
      const fechaHoraFinal = fardos[fardos.length - 1].fechaHora;

      // Estructura de datos a enviar
      console.log('=== DATOS A ENVIAR A ALTA_PRODUCCION ===');
      console.log('arrayDatosCompletos:', arrayDatosCompletos);
      console.log('datosTickets:', datosTickets);
      console.log('detalleTickets:', detalleTickets);
      
      // Validar y preparar los tickets antes de enviar
      let ticketsToSend;
      if (arrayDatosCompletos.length > 0) {
        ticketsToSend = arrayDatosCompletos.map(registro => {
          if (!registro.id_balanza || registro.id_balanza <= 0) {
            throw new Error(`ID de balanza inválido para ticket ${registro.numero_ticket}`);
          }
          return {
            balanza_id: registro.id_balanza,
            numero_ticket: registro.numero_ticket,
            rollos: [{
              id_rollo: registro.id_rollo,
              rollo_identificacion: registro.rollo_identificacion
            }]
          };
        });
      } else if (datosTickets.length > 0) {
        ticketsToSend = datosTickets.map(ticket => {
          if (!ticket.id_balanza || ticket.id_balanza <= 0) {
            throw new Error(`ID de balanza inválido para ticket ${ticket.numero_ticket}`);
          }
          return {
            balanza_id: ticket.id_balanza,
            numero_ticket: ticket.numero_ticket,
            rollos: ticket.rollos.map(rollo => ({
              id_rollo: rollo.id_rollo,
              rollo_identificacion: rollo.rollo_identificacion
            }))
          };
        });
      } else {
        ticketsToSend = detalleTickets.map(ticket => {
          if (!ticket.id || ticket.id <= 0) {
            throw new Error(`ID de ticket inválido para ticket ${ticket.numero_ticket}`);
          }
          return {
            balanza_id: ticket.id,
            numero_ticket: ticket.numero_ticket,
            rollos: [] // Array vacío para tickets sin rollos
          };
        });
      }

      const dataToSend = {
        produccion: {
          cliente_id: clienteId,
          productor_id: productorId,
          producto_id: Number(productoId),
          kilos_semilla: datosA3['Kilos Semilla Calculo'],
          fecha_hora_inicio: fechaHoraInicio,
          fecha_hora_final: fechaHoraFinal,
          resto_positivo: restoPositivo || 0,
          resto_negativo: restoNegativo || 0,
          tipo_registracion: "M"
        },
        ticket: ticketsToSend,
        fardo: fardos.map(fardo => ({
          nro_fardo: fardo.numero,
          kilos_fardo: fardo.kilos,
          kilos_tara: datosA3['Tara p/Fardo'],
          fecha_hora: fardo.fechaHora
        }))
      };

      console.log('Estructura final de tickets a enviar:', dataToSend.ticket);
      console.log('=== FIN DATOS A ENVIAR ===');

      if (!user) {
        console.error('No hay usuario autenticado');
        return;
      }

      const userMetadata = user as unknown as User;
      const id_empresa = userMetadata.id_empresa;
      const id_sucursal = userMetadata.id_sucursal;

      const response = await fetch(
        `${process.env.REACT_APP_PRODUCCION_URL}/alta_produccion/${id_empresa}/${id_sucursal}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(dataToSend)
        }
      );

      const data = await response.json();

      message.destroy();

      if (!response.ok) {
        // Si hay un error HTTP (400, 500, etc.)
        console.error('Error HTTP:', response.status, data);
        let errorMessage = 'Error al grabar la producción';
        
        if (response.status === 400) {
          // Error de validación
          if (data.detalles) {
            errorMessage = `Error de validación: ${JSON.stringify(data.detalles)}`;
            console.error('Detalles del error de validación:', data.detalles);
          } else if (data.message) {
            errorMessage = data.message;
          } else {
            errorMessage = 'Error de validación: Los datos enviados no son válidos. Verifique la consola para más detalles.';
          }
        }
        
        message.error(errorMessage);
        return;
      }

      if (data.status === 'success') {
        message.success('Producción grabada correctamente');
        // Limpiar estados
        setFardos([]);
        setFardoNumero(1);
        setKilosFardo(null);
        setRestoNegativo(null);
        setRestoPositivo(null);
        setModoEdicion(false);
        setFardoSeleccionado(null);
        setIsGrabarTodoEnabled(false);
        
        setTimeout(() => {
          navigate('/a_produccion');
        }, 1500);
      } else {
        message.error(data.message || 'Error al grabar la producción');
      }

    } catch (error) {
      message.destroy();
      console.error('Error al grabar todo:', error);
      message.error('Error al grabar la producción');
    } finally {
      setIsButtonLoading(false); // Desactivar el efecto de carga del botón
    }
  };

  // Agregar el useEffect para obtener el número de fardo
  useEffect(() => {
    const obtenerNumeroFardo = async () => {
      try {
        if (!user) {
          console.error('No hay usuario autenticado');
          return;
        }

        const userMetadata = user as unknown as User;
        const id_empresa = userMetadata.id_empresa;
        const id_sucursal = userMetadata.id_sucursal;

        const token = await getAccessTokenSilently();

        const response = await fetch(
          `${process.env.REACT_APP_PRODUCCION_URL}/ultimo_fardo_desmotado/${id_empresa}/${id_sucursal}/${productoId}/${clienteIdFardo}/${presentacionIdFardo}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error('Error al obtener número de fardo');
        }

        const data = await response.json();
        
        if (data.status === 'success') {
          // Guardar siempre la lista de números existentes en DB (independiente de si hay fardos locales)
          setFardosExistentesDB(data.fardos_existentes || []);
          setFardoProduccionMap(data.fardos_con_produccion || {});
          // Solo actualizar el contador si el usuario aún no agregó fardos en esta sesión.
          // Si ya hay fardos locales, no reiniciar el contador (evita colisión con fardos
          // aún no guardados en DB cuando el effect se re-ejecuta por refresh de token Auth0).
          if (fardosRef.current.length === 0) {
            const nuevoFardo = data.ultimo_fardo + 1;
            setFardoNumero(nuevoFardo);
            setFardoNumeroCopia(nuevoFardo);
            setFardoEsperado(nuevoFardo);
            setFardoValidado(true);
            setPrimerFardoValidado(false);
          }
          console.log('Número de fardo obtenido:', data.ultimo_fardo, '| Existentes:', data.fardos_existentes);
        } else {
          console.error('Error en respuesta:', data.message);
          message.error('Error al obtener el número de fardo');
          setFardoValidado(false);
          setPrimerFardoValidado(false);
        }
      } catch (error) {
        console.error('Error al obtener número de fardo:', error);
        message.error('Error al obtener el número de fardo');
        setFardoValidado(false);
        setPrimerFardoValidado(false);
      }
    };

    if (productoId && clienteIdFardo && presentacionIdFardo) {
      obtenerNumeroFardo();
    }
  // getAccessTokenSilently se omite intencionalmente: su referencia puede cambiar con cada
  // refresh de token de Auth0, lo que causaría re-ejecuciones espurias que resetean fardoNumero.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productoId, clienteIdFardo, presentacionIdFardo]);

  // Agregar función para cambiar el número de fardo
  const handleCambiarNumeroFardo = () => {
    if (!nuevoNumeroFardo || nuevoNumeroFardo <= 0) {
      message.error('Ingrese un número de fardo válido');
      return;
    }
    if (fardos.some(f => f.numero === nuevoNumeroFardo)) {
      message.error(`El fardo N° ${nuevoNumeroFardo} ya fue grabado en esta sesión`);
      return;
    }
    // Verificar pertenencia exacta a la lista de fardos existentes en DB
    // (reemplaza la comparación con MAX, que bloqueaba números en huecos válidos)
    if (fardosExistentesDB.includes(nuevoNumeroFardo)) {
      const nroProd = fardoProduccionMap[nuevoNumeroFardo];
      const detalle = nroProd ? ` (Producción N° ${nroProd})` : '';
      message.error(`El fardo N° ${nuevoNumeroFardo} ya existe en la base de datos${detalle}`);
      return;
    }
    setFardoNumero(nuevoNumeroFardo);
    setNuevoNumeroFardo(null);
    message.success('Número de fardo actualizado');
  };

  // Función para obtener la tara del fardo
  const fetchTaraFardo = async () => {
    try {
      if (!user) {
        console.error('No hay usuario autenticado');
        return;
      }

      const userMetadata = user as unknown as User;
      const id_empresa = userMetadata.id_empresa;
      const id_sucursal = userMetadata.id_sucursal;

      console.log('Datos de usuario:', { id_empresa, id_sucursal });

      const token = await getAccessTokenSilently();
      const response = await axios.get<TaraFardo[]>(
        `${process.env.REACT_APP_RELACION_URL}/tara_fardo/${id_empresa}/${id_sucursal}/${productoId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data && response.data.length > 0) {
        setDatosA3(prevDatos => ({
          ...prevDatos,
          'Tara p/Fardo': response.data[0].tara
        }));
        console.log('Tara cargada:', response.data[0].tara);
      } else {
        console.log('No se encontró tara para el producto');
        message.warning('No se encontró tara para el producto');
      }

    } catch (error) {
      console.error('Error al obtener tara del fardo:', error);
      message.error('Error al cargar tara del fardo');
    }
  };

  // Llamar a fetchTaraFardo cuando cambie el productoId o el usuario
  useEffect(() => {
    if (productoId && user) {
      fetchTaraFardo();
    }
  }, [productoId, user]);

  // Cargar cliente cero al iniciar
  useEffect(() => {
    if (user) {
      fetchClienteCero();
    }
  }, [user]);

  // Función para obtener el cliente con nc='000000'
  const fetchClienteCero = async () => {
    try {
      if (!user) {
        console.error('No hay usuario autenticado');
        return;
      }

      const userMetadata = user as unknown as User;
      const id_empresa = userMetadata.id_empresa;

      const token = await getAccessTokenSilently();
      const response = await axios.get(
        `${process.env.REACT_APP_RELACION_URL}/cliente_cero/${id_empresa}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data) {
        setClienteCero(response.data);
        console.log('Cliente cero cargado:', response.data);
      }
    } catch (error) {
      console.error('Error al obtener cliente cero:', error);
      // No mostrar error al usuario ya que es opcional
    }
  };

  const styles = {
    layout: {
      minHeight: '100vh',
      padding: '20px',
    },
    areaTop: {
      width: '100%',
      minHeight: '200px',
       maxHeight: '300px',
      backgroundColor: '#1976D2',
      marginBottom: '20px',
      borderRadius: '8px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      padding: '15px',
       overflow: 'auto',
    },
    titleContainer: {
      marginBottom: '15px',
      color: 'white',
      width: '100%',
      textAlign: 'center' as const,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    ticketHeader: {
      fontWeight: 'bold',
       backgroundColor: 'rgba(255, 255, 255, 0.95)',
       padding: '12px 8px',
      borderRadius: '4px',
      marginBottom: '10px',
       display: 'flex',
       alignItems: 'center',
       minHeight: '45px',
       fontSize: '15px',
       color: '#1976D2',
    },
    ticketRow: {
      display: 'flex',
      justifyContent: 'space-between',
       padding: '10px 8px',
      fontSize: '14px',
      backgroundColor: 'rgba(255, 255, 255, 0.8)',
       marginBottom: '3px',
      borderRadius: '4px',
      border: 'none',
       alignItems: 'center',
       minHeight: '40px',
    },
    middleArea: {
      height: '400px',
      borderRadius: '8px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      padding: '10px',
      overflow: 'auto',
      backgroundColor: '#BBDEFB',
    },
    areaBottom: {
      width: '100%',
      height: '60px',
      backgroundColor: '#2196F3',
      marginBottom: '20px',
      borderRadius: '8px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      padding: '10px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  };

  // Limpiar el intervalo cuando el componente se desmonte
  useEffect(() => {
    return () => {
      // Cleanup function
    };
  }, []);
  
  // NO limpiar automáticamente los datos del contexto
  // Los datos se mantendrán hasta que se complete el proceso

  // Componente del reloj de espera mejorado
  const RelojEspera = () => (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '40px',
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      borderRadius: '12px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      margin: '20px 0'
    }}>
      <div style={{
        fontSize: '48px',
        color: '#1976D2',
        marginBottom: '20px',
        display: 'flex',
        alignItems: 'center',
        gap: '15px'
      }}>
        <ClockCircleOutlined spin />
        <span style={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
          {formatearTiempoEspera(tiempoEspera)}
        </span>
      </div>
      
      <div style={{
        fontSize: '24px',
        color: '#1976D2',
        marginBottom: '15px',
        textAlign: 'center'
      }}>
        ⏳ Esperando datos de tickets...
      </div>
      
      {fardoEsperado && (
        <div style={{
          fontSize: '18px',
          color: '#666',
          marginBottom: '20px',
          textAlign: 'center',
          padding: '10px 20px',
          backgroundColor: '#f0f8ff',
          borderRadius: '8px',
          border: '2px solid #1976D2'
        }}>
          📦 Próximo fardo a procesar: <strong>Nº {fardoEsperado}</strong>
        </div>
      )}

      {/* Estado de validación del fardo */}
      <div style={{
        fontSize: '16px',
        color: primerFardoValidado ? '#52c41a' : '#faad14',
        marginBottom: '15px',
        textAlign: 'center',
        padding: '8px 16px',
        backgroundColor: primerFardoValidado ? '#f6ffed' : '#fffbe6',
        borderRadius: '6px',
        border: `1px solid ${primerFardoValidado ? '#b7eb8f' : '#ffe58f'}`
      }}>
        {validandoFardo ? (
          <span>🔄 Validando primer fardo...</span>
        ) : primerFardoValidado ? (
          <span>✅ Primer fardo validado</span>
        ) : (
          <span>⚠️ Validar primer fardo al grabar</span>
        )}
      </div>
      
      <div style={{
        fontSize: '14px',
        color: '#888',
        textAlign: 'center',
        maxWidth: '400px',
        lineHeight: '1.5'
      }}>
        El sistema está esperando que se seleccionen los tickets desde la pantalla de A_Produccion.
        Una vez que se envíen los datos, se mostrará automáticamente la información de producción.
        <br /><br />
        <strong>Importante:</strong> Solo se valida el primer fardo. Los siguientes incrementan automáticamente.
      </div>
      
      <div style={{
        marginTop: '20px',
        display: 'flex',
        gap: '10px'
      }}>
        <Button 
          type="primary" 
          onClick={() => navigate('/a_produccion')}
          icon={<LoadingOutlined />}
          size="large"
        >
          Ir a Selección de Tickets
        </Button>
        <Button 
          onClick={async () => {
            console.log('Validando primer fardo manualmente...');
            await validarNumeroFardo();
          }}
          loading={validandoFardo}
          size="large"
        >
          Validar Primer Fardo
        </Button>
        <Button 
          onClick={() => {
            console.log('Limpiando contexto manualmente');
            clearProduccionData();
            setMostrarRelojEspera(false);
            setTiempoEspera(0);
            setFardoValidado(false);
            setPrimerFardoValidado(false);
          }}
          size="large"
        >
          Limpiar Contexto
        </Button>
      </div>
    </div>
  );

  return (
    <Layout style={styles.layout}>
      {/* A1 - Superior */}
      <div style={styles.areaTop}>
        <div style={styles.titleContainer}>
          <Title level={3} style={{ 
            color: 'white', 
            margin: 0,
            textAlign: 'center',
            width: '100%'
          }}>
            DESMOTE: {productoDesc || 'Cargando...'} {productoNivel && `(Nivel: ${productoNivel})`}
          </Title>
        </div>
        
        {/* Mostrar reloj de espera o datos según corresponda */}
        {mostrarRelojEspera ? (
          <RelojEspera />
        ) : (
          <>
            {/* Encabezado de columnas */}
            <Row style={styles.ticketHeader}>
                <Col span={2} style={{ textAlign: 'center' }}><strong>Ticket</strong></Col>
                <Col span={3} style={{ textAlign: 'center' }}><strong>Rollo</strong></Col>
                <Col span={3} style={{ textAlign: 'center' }}><strong>Fecha</strong></Col>
                <Col span={4} style={{ textAlign: 'left', paddingLeft: '8px' }}><strong>Cliente</strong></Col>
                <Col span={4} style={{ textAlign: 'left', paddingLeft: '8px' }}><strong>Productor</strong></Col>
                <Col span={3} style={{ textAlign: 'center' }}><strong>Producto</strong></Col>
                <Col span={3} style={{ textAlign: 'right' }}><strong>Kilos</strong></Col>
            </Row>

            {/* Filas de datos usando arrayDatosCompletos */}
            {arrayDatosCompletos.length > 0 ? (
              (() => {
                console.log('✅ Renderizando arrayDatosCompletos:', arrayDatosCompletos);
                console.log('Estado de produccionData en renderizado:', !!produccionData);
                return arrayDatosCompletos.map((registro, index) => (
                  <Row key={index} style={{
                    ...styles.ticketRow,
                    backgroundColor: index % 2 === 0 
                      ? 'rgba(255, 255, 255, 0.9)' 
                      : 'rgba(255, 255, 255, 0.7)'
                  }}>
                    <Col span={2} style={{ textAlign: 'center', fontWeight: 'bold' }}>
                      {registro.numero_ticket}
                    </Col>
                    <Col span={3} style={{ textAlign: 'center' }}>
                      {registro.rollo_identificacion || '-'}
                    </Col>
                    <Col span={3} style={{ textAlign: 'center' }}>
                      {registro.fecha}
                    </Col>
                    <Col span={4} style={{ textAlign: 'left', paddingLeft: '8px' }}>
                      {registro.cliente}
                    </Col>
                    <Col span={4} style={{ textAlign: 'left', paddingLeft: '8px' }}>
                      {registro.productor}
                    </Col>
                    <Col span={3} style={{ textAlign: 'center' }}>
                      {registro.producto}
                    </Col>
                    <Col span={3} style={{ textAlign: 'right', fontWeight: 'bold' }}>
                      {registro.kilos.toLocaleString()} kg
                    </Col>
                  </Row>
                ));
              })()
            ) : (
              // Mostrar mensaje cuando no hay datos pero no está en modo espera
              <Row style={styles.ticketRow}>
                <Col span={24} style={{ textAlign: 'center', padding: '20px' }}>
                  <div style={{ color: 'white', fontSize: '16px' }}>
                    📋 No hay datos de tickets para mostrar
                  </div>
                </Col>
              </Row>
            )}
          </>
        )}
      </div>

      {/* A2, A3, A4 - Medio - Solo mostrar si hay datos */}
      {!mostrarRelojEspera && (
        <Row gutter={[20, 20]} style={{ marginBottom: '20px' }}>
          <Col span={8}>
            <div style={{
              ...styles.middleArea,
              backgroundColor: '#1976D2',
            }}>
              <Card 
                title={
                  <div style={{ 
                    color: 'white', 
                    textAlign: 'center',
                    width: '100%',
                    fontWeight: 'bold',
                    fontSize: '16px'
                  }}>
                    PRODUCCIÓN FARDOS
                  </div>
                }
                style={{ 
                  height: '100%',
                  backgroundColor: 'transparent',
                  border: 'none'
                }}
                headStyle={{
                  backgroundColor: 'transparent',
                  border: 'none'
                }}
                bodyStyle={{
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  padding: '16px'
                }}
              >
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ marginBottom: '8px' }}>
                    <strong>Fardo Nº:</strong> 
                    <span style={{ 
                      marginLeft: '8px',
                      color: fardos.length === 0 ? (primerFardoValidado ? '#52c41a' : '#faad14') : '#52c41a',
                      fontWeight: 'bold'
                    }}>
                      {fardoNumero}
                      {fardos.length === 0 ? (primerFardoValidado ? ' ✅' : ' ⚠️') : ' ✅'}
                    </span>
                  </div>
                  <div style={{ marginBottom: '16px' }}>
                    <strong>Kilos:</strong>
                    <InputNumber
                      style={{ width: '100%', marginTop: '4px' }}
                      value={kilosFardo}
                      onChange={(value) => setKilosFardo(value)}
                      placeholder="Ingrese los kilos"
                      min={0.01}
                      precision={2}
                      onPressEnter={() => modoEdicion ? modificarFardo() : grabarFardo()}
                    />
                  </div>
                  <div style={{ 
                    display: 'flex', 
                    gap: '8px',
                    justifyContent: 'center',
                    marginBottom: '20px'
                  }}>
                    <Button 
                      type="primary"
                      onClick={grabarFardo}
                      disabled={modoEdicion}
                    >
                      Grabar
                    </Button>
                    <Button 
                      type="primary"
                      onClick={modificarFardo}
                      disabled={!modoEdicion}
                    >
                      Modificar
                    </Button>
                    <Button 
                      type="primary"
                      onClick={grabarTodo}
                      loading={isButtonLoading}
                      disabled={!isGrabarTodoEnabled}
                      danger
                      style={{
                        backgroundColor: '#f5222d',
                        borderColor: '#f5222d',
                        minWidth: '150px',
                        height: '40px',
                        fontSize: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      Grabar Todo
                    </Button>
                  </div>
                  
                  {/* Botón de validación manual solo para el primer fardo */}
                  {fardos.length === 0 && (
                    <div style={{ textAlign: 'center', marginBottom: '10px' }}>
                      <Button 
                        onClick={validarNumeroFardo}
                        loading={validandoFardo}
                        size="small"
                        type="dashed"
                      >
                        {validandoFardo ? 'Validando...' : 'Validar Primer Fardo'}
                      </Button>
                    </div>
                  )}
                </div>

                <div style={{ 
                  borderTop: '1px solid #1565C0', 
                  margin: '10px 0 20px 0' 
                }} />

                <div style={{
                  backgroundColor: '#1565C0',
                  padding: '15px',
                  borderRadius: '8px',
                }}>
                  <div style={{ 
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '10px',
                    marginBottom: '15px'
                  }}>
                    <div>
                      <label style={{ 
                        color: 'white', 
                        marginBottom: '5px', 
                        display: 'block' 
                      }}>
                        Resto (-)
                      </label>
                      <InputNumber
                        style={{ width: '100%' }}
                        value={restoNegativo}
                        onChange={(value) => setRestoNegativo(value)}
                        min={0}
                        precision={2}
                      />
                    </div>
                    <div>
                      <label style={{ 
                        color: 'white', 
                        marginBottom: '5px', 
                        display: 'block' 
                      }}>
                        Resto (+)
                      </label>
                      <InputNumber
                        style={{ width: '100%' }}
                        value={restoPositivo}
                        onChange={(value) => setRestoPositivo(value)}
                        min={0}
                        precision={2}
                      />
                    </div>
                  </div>

                </div>
              </Card>
            </div>
          </Col>
          <Col span={8}>
            <div style={{
              ...styles.middleArea,
              backgroundColor: '#1976D2',
            }}>
              <Card 
                title={
                  <div style={{ 
                    color: 'white', 
                    textAlign: 'center',
                    width: '100%',
                    fontWeight: 'bold',
                    fontSize: '16px'
                  }}>
                    RESUMEN
                  </div>
                }
                style={{ 
                  height: '100%',
                  backgroundColor: 'transparent',
                  border: 'none'
                }}
                headStyle={{
                  backgroundColor: 'transparent',
                  border: 'none'
                }}
                bodyStyle={{
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  padding: '16px'
                }}
              >
                {Object.entries(datosA3).map(([key, value]) => (
                  <div key={key} style={{
                    marginBottom: '8px',
                    fontSize: '14px',
                    display: 'flex',
                    justifyContent: 'space-between'
                  }}>
                    <strong>{key}:</strong> 
                    <span>{value}</span>
                  </div>
                ))}
                
                {/* Información adicional sobre el cálculo de semilla (productos FIBRA) */}
                <div style={{
                  marginTop: '16px',
                  padding: '12px',
                  backgroundColor: productoDesc === 'FIBRA' ? '#e6f7ff' : '#f6ffed',
                  borderRadius: '6px',
                  border: `1px solid ${productoDesc === 'FIBRA' ? '#91d5ff' : '#b7eb8f'}`
                }}>
                  <div style={{
                    fontSize: '12px',
                    fontWeight: 'bold',
                    marginBottom: '4px',
                    color: productoDesc === 'FIBRA' ? '#1890ff' : '#52c41a'
                  }}>
                    Estado Cálculo Semilla:
                  </div>
                  <div style={{ fontSize: '12px', marginBottom: '8px' }}>
                    {productoDesc === 'FIBRA'
                      ? `FIBRA: Cálculo activo (${datosA3['Kilos Semilla Calculo']} kg)`
                      : `${productoDesc || 'Producto'}: Cálculo desactivado (0 kg)`
                    }
                  </div>
                  <div style={{
                    fontSize: '11px',
                    color: productoDesc === 'FIBRA' ? '#1890ff' : '#52c41a',
                    fontStyle: 'italic'
                  }}>
                    {productoDesc === 'FIBRA'
                      ? `Cliente: ${detalleTickets[0]?.cliente?.razon || 'Original'}`
                      : `Cliente: ${clienteCero?.denominacion || 'NC="000000"'}`
                    }
                  </div>
                </div>
              </Card>
            </div>
          </Col>
          <Col span={8}>
            <div style={{
              ...styles.middleArea,
              backgroundColor: '#1976D2',
            }}>
              <Card 
                title={
                  <div style={{ 
                    color: 'white', 
                    textAlign: 'center',
                    width: '100%',
                    fontWeight: 'bold',
                    fontSize: '16px'
                  }}>
                    DETALLE PRODUCCIÓN
                  </div>
                }
                style={{ 
                  height: '100%',
                  backgroundColor: 'transparent',
                  border: 'none'
                }}
                headStyle={{
                  backgroundColor: 'transparent',
                  border: 'none'
                }}
                bodyStyle={{
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  padding: '16px'
                }}
              >
                <div style={{ marginBottom: '16px' }}>
                  <Radio.Group 
                    value={fardoSeleccionado}
                    onChange={(e) => {
                      const numeroFardo_copia=fardoNumero;
                      console.log('numeroFardo_copia:',numeroFardo_copia);
                      setFardoSeleccionado(e.target.value);
                      handleSeleccionFardo(e.target.value);
                    }}
                    style={{ width: '100%' }}
                  >
                    {fardos.map(fardo => (
                      <div 
                        key={fardo.numero}
                        style={{
                          padding: '8px',
                          marginBottom: '8px',
                          backgroundColor: fardoSeleccionado === fardo.numero ? '#e6f7ff' : 'transparent',
                          borderRadius: '4px',
                          display: 'grid',
                          gridTemplateColumns: 'auto 1fr 1fr 2fr',
                          gap: '10px',
                          alignItems: 'center',
                          fontSize: '14px'
                        }}
                      >
                        <Radio value={fardo.numero} />
                        <span style={{ textAlign: 'center' }}>{fardo.numero}</span>
                        <span style={{ textAlign: 'center' }}>{fardo.kilos}</span>
                        <span style={{ textAlign: 'center' }}>{fardo.fechaHora}</span>
                      </div>
                    ))}
                  </Radio.Group>
                </div>
              </Card>
            </div>
          </Col>
        </Row>
      )}

      {/* Input y botón para cambiar número de fardo - Solo mostrar si no está en modo espera */}
      {!mostrarRelojEspera && (
        <Row gutter={16} style={{ marginTop: '20px' }}>
          <Col span={24}>
            <Card>
              <Space>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span style={{ marginRight: '10px', fontWeight: 'bold' }}>
                    NUEVO Nº Fardo:
                  </span>
                  <InputNumber
                    value={nuevoNumeroFardo}
                    onChange={(value) => setNuevoNumeroFardo(value)}
                    style={{ width: '150px' }}
                    min={1}
                    precision={0}
                  />
                  <Button 
                    type="primary" 
                    danger
                    onClick={handleCambiarNumeroFardo}
                    style={{ marginLeft: '10px' }}
                  >
                    CAMBIAR
                  </Button>
                </div>
              </Space>
            </Card>
          </Col>
        </Row>
      )}

      {/* A5 - Inferior */}
      <div style={styles.areaBottom}>
        <Title level={4} style={{ 
          color: 'white', 
          margin: 0,
          lineHeight: '40px'
        }}>
          Panel de Control - {productoDesc}
        </Title>
      </div>
    </Layout>
  );
};

export default ProduccionLayout;